import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flag, Loader2, X, Check } from 'lucide-react';
import { reportPrayer } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * "Report Misuse" button.
 *
 * Follows the standard content-report pattern: a flag icon that opens a
 * small dialog for an optional reason. On submit we POST to
 * `/api/prayers/{id}/report` and show a "Thanks — a moderator will review"
 * confirmation. Signed-out users are pushed to the auth modal first.
 *
 * Props:
 *   prayerId     — required
 *   variant      — 'card' (subtle button for feed cards) | 'inline' (larger,
 *                  used next to candle/share on the watch page)
 *   size         — icon pixel size (defaults per-variant)
 */
const ReportPrayerButton = ({
  prayerId,
  variant = 'inline',
  size,
  isOwner = false,
  // Optional controlled-open mode. When provided, the caller owns the
  // dialog visibility so it can close the menu + open the dialog in the
  // same handler (see PrayerCard's ⋮ menu → Report Misuse row).
  open: controlledOpen,
  onOpenChange,
}) => {
  const { user, openAuthModal } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean' && typeof onOpenChange === 'function';
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [errText, setErrText] = useState('');

  useEffect(() => {
    if (!open) {
      // Reset transient dialog state after close animation.
      const t = setTimeout(() => {
        setReason('');
        setStatus(null);
        setErrText('');
        setSubmitting(false);
      }, 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Never show on the author's own prayer — they have Delete instead.
  if (isOwner) return null;

  const handleOpen = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user) { openAuthModal(); return; }
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrText('');
    try {
      await reportPrayer(prayerId, reason.trim());
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrText(err?.response?.data?.detail || err.message || 'Could not submit report.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Trigger button ----------
  const iconSize = size || (variant === 'card' ? 16 : variant === 'compact' ? 14 : variant === 'menuitem' ? 15 : 24);
  const trigger = variant === 'card' ? (
    <button
      type="button"
      onClick={handleOpen}
      data-testid={`report-btn-${prayerId}`}
      aria-label="Report misuse"
      title="Report misuse"
      className="w-9 h-9 rounded-full bg-black/55 hover:bg-red-600/80 text-white backdrop-blur flex items-center justify-center transition-transform active:scale-90"
    >
      <Flag size={iconSize} />
    </button>
  ) : variant === 'compact' ? (
    // Compact icon matching the metadata-anchor action row on prayer
    // cards: 28x28 hit area, subtle base color, red tint on hover.
    <button
      type="button"
      onClick={handleOpen}
      data-testid={`report-btn-${prayerId}`}
      aria-label="Report misuse"
      title="Report misuse"
      className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors active:scale-95"
    >
      <Flag size={iconSize} strokeWidth={2.25} />
    </button>
  ) : variant === 'menuitem' ? (
    // Full-width menu row styled to match shadcn's DropdownMenuItem so it
    // slots into the ⋮ dropdown on prayer cards. Left-aligned icon + label.
    <button
      type="button"
      onClick={handleOpen}
      data-testid={`report-btn-${prayerId}`}
      role="menuitem"
      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm text-zinc-700 dark:text-zinc-200 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-400 focus:outline-none focus:bg-red-50 dark:focus:bg-red-950/40 transition-colors"
    >
      <Flag size={iconSize} strokeWidth={2.25} />
      <span>Report Misuse</span>
    </button>
  ) : (
    <button
      type="button"
      onClick={handleOpen}
      data-testid={`report-btn-${prayerId}`}
      aria-label="Report misuse"
      title="Report misuse"
      className="p-2.5 rounded-full hover:bg-red-50 dark:hover:bg-red-950/30 text-zinc-500 hover:text-red-500 transition-transform active:scale-90"
    >
      <Flag size={iconSize} strokeWidth={2.25} />
    </button>
  );

  return (
    <>
      {!isControlled && trigger}

      {open && createPortal((
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 isolate"
          onClick={() => setOpen(false)}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="report-dialog-overlay"
        >
          <div
            onClick={(e) => { e.stopPropagation(); }}
            className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl overflow-hidden"
            data-testid="report-dialog"
          >
            <div className="flex items-start justify-between px-5 pt-4 pb-2">
              <div>
                <div className="flex items-center gap-2 text-lg font-semibold text-zinc-900 dark:text-white">
                  <Flag size={18} className="text-red-500" /> Report misuse
                </div>
                <p className="text-sm text-zinc-500 mt-0.5">
                  A moderator will review this prayer.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="report-close-btn"
                aria-label="Close"
                className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500"
              >
                <X size={18} />
              </button>
            </div>

            {status === 'success' ? (
              <div className="px-5 pb-5 pt-2 space-y-3">
                <div
                  className="flex items-center gap-2 text-sm rounded-lg px-3 py-2 border text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900"
                  data-testid="report-success"
                >
                  <Check size={16} /> Thanks — a moderator will review shortly.
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="report-done-btn"
                  className="w-full h-10 rounded-xl bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-semibold"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-5 pb-5 pt-2 space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                    Why are you reporting this? (optional)
                  </div>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    maxLength={800}
                    placeholder="Describe the misuse - inappropriate content, spam, harassment, impersonation or anything else that concerns you."
                    data-testid="report-reason"
                    className="w-full rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 outline-none focus:border-red-400 dark:focus:border-red-500 px-3 py-2 text-sm text-zinc-900 dark:text-white placeholder-zinc-400 resize-none"
                  />
                  <div className="text-[11px] text-zinc-500 mt-1 text-right tabular-nums">
                    {reason.length}/800
                  </div>
                </label>

                {status === 'error' && (
                  <div
                    className="text-sm rounded-lg px-3 py-2 border text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900"
                    data-testid="report-error"
                  >
                    {errText || 'Could not submit report.'}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 h-10 rounded-xl border border-zinc-300 dark:border-zinc-700 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="report-submit-btn"
                    className="flex-1 h-10 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Report
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ), document.body)}
    </>
  );
};

export default ReportPrayerButton;
